import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllPizzas,
  getSinglePizza,
  createPizza,
  updatePizza,
  deletePizza,
} from "@/api/new-api/staff-management/menu-manage/pizza";
import { websiteOrderingAvailableItemsToggle } from "@/api/new-api/staff-management/menu-manage/drink";

// Thunks

export const fetchAllPizzas = createAsyncThunk(
  "pizzas/fetchAll",
  async (restaurant_id, { rejectWithValue }) => {
    const res = await getAllPizzas(restaurant_id);
    if (res.status === "success") return res.data.pizzas;
    return rejectWithValue(res.message);
  }
);

export const fetchSinglePizza = createAsyncThunk(
  "pizzas/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSinglePizza(id);
    if (res.status === "success") return res.data.pizza;
    return rejectWithValue(res.message);
  }
);

export const createNewPizza = createAsyncThunk(
  "pizzas/create",
  async (data, { rejectWithValue }) => {
    const res = await createPizza(data);
    if (res.status === "success") return res.data.pizza;
    return rejectWithValue(res.message);
  }
);

export const updateExistingPizza = createAsyncThunk(
  "pizzas/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updatePizza(id, data);
    if (res.status === "success") return res.data.pizza;
    return rejectWithValue(res.message);
  }
);

export const deletePizzaById = createAsyncThunk(
  "pizzas/delete",
  async (id, { rejectWithValue }) => {
    const res = await deletePizza(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

export const updatePizzaToggle = createAsyncThunk("pizzaToggle/update", async ({ id, type, toggleAvailabilityType }, { rejectWithValue }) => {
    const res = await websiteOrderingAvailableItemsToggle(id, type, toggleAvailabilityType);
    if (res.status === "success") return res.data;
    return rejectWithValue(res.message);
});

// Initial State

const initialState = {
  pizzas: [],
  singlePizza: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice

const pizzaSlice = createSlice({
  name: "pizzas",
  initialState,
  reducers: {
    clearPizzaMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder

      // Fetch All
      .addCase(fetchAllPizzas.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllPizzas.fulfilled, (state, action) => {
        state.getLoading = false;
        state.pizzas = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllPizzas.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch Single
      .addCase(fetchSinglePizza.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchSinglePizza.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singlePizza = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSinglePizza.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewPizza.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(createNewPizza.fulfilled, (state, action) => {
        state.createLoading = false;
        state.pizzas.push(action.payload);
        state.success = "Pizza created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewPizza.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingPizza.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateExistingPizza.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.pizzas = state.pizzas.map((pizza) =>
          pizza.id === action.payload.id ? action.payload : pizza
        );
        state.success = "Pizza updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingPizza.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Delete
      .addCase(deletePizzaById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(deletePizzaById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.pizzas = state.pizzas.filter(
          (pizza) => pizza.id !== action.payload
        );
        state.success = "Pizza deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deletePizzaById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // toggle website/QRcode availability
      .addCase(updatePizzaToggle.pending, (state) => {
          state.updateLoading = true;
          state.error = null;
          state.requestStatus = false;
      })
      .addCase(updatePizzaToggle.fulfilled, (state, action) => {
        state.updateLoading = false;

        const { id, toggleAvailabilityType } = action.meta.arg;

        state.pizzas = state.pizzas.map((drink) => {
          if (drink.id !== id) return drink;

          if (toggleAvailabilityType === "qr_code") {
            return {
              ...drink,
              is_qr_code_order_available: !drink.is_qr_code_order_available,
            };
          }

          return {
            ...drink,
            is_website_order_available: !drink.is_website_order_available,
          };
        });

        state.success = "Updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updatePizzaToggle.rejected, (state, action) => {
          state.updateLoading = false;
          state.error = action.payload;
          state.requestStatus = false;
      })
  },
});

export const { clearPizzaMessages } = pizzaSlice.actions;
export default pizzaSlice.reducer;
